import { normalizeInvoiceRole } from '../../modules/docs/utils/invoiceLedger';
import { toDateOnly } from '../../modules/docs/utils/invoicePaymentTerms';
import { isInstallDone } from '../job-records/jobReview.model';
import type {
  OpsDocument,
  OpsProject,
  OpsProjectEvent,
  OpsShareLink,
} from './workspaceDashboard.model';

/**
 * Operational View: a read-only interpretation of project facts.
 * The Project Workflow checklist stays the manual record; nothing here is persisted.
 */

export type OperationalQueueId = 'presale' | 'deposit' | 'ordering' | 'installation' | 'aftercare';

export type OperationalStateId =
  | 'enquiry'
  | 'quoted'
  | 'awaiting_deposit_request'
  | 'deposit_requested'
  | 'deposit_received_not_ordered'
  | 'accepted_not_ordered'
  | 'ordered_awaiting_production'
  | 'production_confirmed_not_booked'
  | 'booked_order_not_placed'
  | 'booked_deposit_not_received'
  | 'installation_booked'
  | 'installed';

type OperationalStateMeta = {
  queue: OperationalQueueId;
  /** Chip / count label. */
  short: string;
  label: string;
  /** Supply-only wording where the booking is a delivery. */
  deliveryLabel?: string;
  exception: boolean;
};

export const OPERATIONAL_STATES: Record<OperationalStateId, OperationalStateMeta> = {
  enquiry: { queue: 'presale', short: 'Enquiry', label: 'Enquiry · No quote', exception: false },
  quoted: { queue: 'presale', short: 'Quoted', label: 'Quoted · Awaiting acceptance', exception: false },
  awaiting_deposit_request: {
    queue: 'deposit',
    short: 'Awaiting deposit request',
    label: 'Awaiting deposit request',
    exception: false,
  },
  deposit_requested: {
    queue: 'deposit',
    short: 'Deposit requested',
    label: 'Deposit request issued',
    exception: false,
  },
  deposit_received_not_ordered: {
    queue: 'deposit',
    short: 'Deposit received, not ordered',
    label: 'Deposit received · Not ordered',
    exception: false,
  },
  accepted_not_ordered: {
    queue: 'deposit',
    short: 'Accepted, not ordered',
    label: 'Accepted · No deposit · Not ordered',
    exception: false,
  },
  ordered_awaiting_production: {
    queue: 'ordering',
    short: 'Awaiting production date',
    label: 'Ordered · Awaiting production date',
    exception: false,
  },
  production_confirmed_not_booked: {
    queue: 'ordering',
    short: 'Production confirmed, not scheduled',
    label: 'Production date confirmed · Installation not booked',
    deliveryLabel: 'Production date confirmed · Delivery not booked',
    exception: false,
  },
  booked_order_not_placed: {
    queue: 'installation',
    short: 'Booked, order not placed',
    label: 'Installation booked · Order not placed',
    deliveryLabel: 'Delivery booked · Order not placed',
    exception: true,
  },
  booked_deposit_not_received: {
    queue: 'installation',
    short: 'Booked, deposit not received',
    label: 'Installation booked · Deposit not received',
    deliveryLabel: 'Delivery booked · Deposit not received',
    exception: true,
  },
  installation_booked: {
    queue: 'installation',
    short: 'Installation booked',
    label: 'Installation booked',
    deliveryLabel: 'Delivery booked',
    exception: false,
  },
  installed: { queue: 'aftercare', short: 'Installed', label: 'Installed · Closing out', exception: false },
};

export const OPERATIONAL_QUEUES: Array<{ id: OperationalQueueId; label: string; states: OperationalStateId[] }> = [
  { id: 'presale', label: 'Pre-sale', states: ['enquiry', 'quoted'] },
  {
    id: 'deposit',
    label: 'Deposit',
    states: ['awaiting_deposit_request', 'deposit_requested', 'deposit_received_not_ordered', 'accepted_not_ordered'],
  },
  { id: 'ordering', label: 'Ordering', states: ['ordered_awaiting_production', 'production_confirmed_not_booked'] },
  {
    id: 'installation',
    label: 'Installation',
    states: ['booked_order_not_placed', 'booked_deposit_not_received', 'installation_booked'],
  },
  { id: 'aftercare', label: 'Aftercare', states: ['installed'] },
];

export type OperationalFilter =
  | { kind: 'all' }
  | { kind: 'queue'; id: OperationalQueueId }
  | { kind: 'state'; id: OperationalStateId };

export function matchesOperationalFilter(
  filter: OperationalFilter,
  row: { queue: OperationalQueueId; state: OperationalStateId },
): boolean {
  if (filter.kind === 'queue') return row.queue === filter.id;
  if (filter.kind === 'state') return row.state === filter.id;
  return true;
}

export function operationalStateLabel(id: OperationalStateId, supplyOnly: boolean): string {
  const meta = OPERATIONAL_STATES[id];
  return supplyOnly && meta.deliveryLabel ? meta.deliveryLabel : meta.label;
}

function statusOf(value?: string | null) {
  return String(value || '').trim().toLowerCase();
}

export function isPaidStatus(status?: string | null) {
  const normalized = statusOf(status);
  return normalized === 'paid' || normalized === 'cash';
}

export function isLiveIssuedInvoice(doc: OpsDocument): boolean {
  if (String(doc.kind || '') !== 'Invoice') return false;
  const status = statusOf(doc.status);
  return status !== 'draft' && status !== 'void' && status !== 'rejected';
}

export function isIssuedUnpaidInvoice(doc: OpsDocument): boolean {
  return isLiveIssuedInvoice(doc) && !isPaidStatus(doc.status);
}

const ACCEPTED_QUOTE_STATUSES = new Set(['accepted', 'invoiced', 'paid', 'cash']);

function isQuoteLike(doc: OpsDocument) {
  return doc.kind === 'Quotation' || doc.kind === 'Estimate';
}

function stampMs(value?: string | null) {
  return Date.parse(value || '') || 0;
}

export function latestQuote(docs: OpsDocument[]): OpsDocument | null {
  const rows = docs.filter(isQuoteLike);
  if (!rows.length) return null;
  return rows.reduce((latest, row) => (
    stampMs(row.created_at || row.sent_at) >= stampMs(latest.created_at || latest.sent_at) ? row : latest
  ));
}

/**
 * The quote the customer actually committed to. With several options only one is
 * accepted, so the newest quote is not necessarily it. A recorded `accepted_at` still
 * counts when the status was later moved back to Sent by a Final transmission.
 */
export function acceptedQuote(docs: OpsDocument[]): OpsDocument | null {
  const rows = docs.filter((doc) => {
    if (!isQuoteLike(doc)) return false;
    const status = statusOf(doc.status);
    if (status === 'rejected' || status === 'void') return false;
    return ACCEPTED_QUOTE_STATUSES.has(status) || !!doc.accepted_at;
  });
  if (!rows.length) return null;
  return rows.reduce((best, row) => {
    const bestRank = best.kind === 'Quotation' ? 1 : 0;
    const rowRank = row.kind === 'Quotation' ? 1 : 0;
    if (rowRank !== bestRank) return rowRank > bestRank ? row : best;
    return stampMs(row.accepted_at || row.created_at) >= stampMs(best.accepted_at || best.created_at) ? row : best;
  });
}

function earliest(values: Array<string | null | undefined>): string | null {
  const stamps = values.filter((value): value is string => !!value && stampMs(value) > 0);
  if (!stamps.length) return null;
  return stamps.reduce((min, value) => (stampMs(value) < stampMs(min) ? value : min));
}

function earliestProductionDate(project: OpsProject): string | null {
  const tasks = Array.isArray(project.workflow_tasks) ? project.workflow_tasks : [];
  const dates = tasks
    .filter((task) => task.task === 'Production Date')
    .flatMap((task) => task.productionDates || [])
    .map((entry) => toDateOnly(entry.date))
    .filter((date): date is string => !!date)
    .sort();
  return dates[0] || null;
}

function activeBookingDate(events: OpsProjectEvent[], supplyOnly: boolean): string | null {
  const type = supplyOnly ? 'deliveryDateConfirmation' : 'installationDateConfirmation';
  const dated = events.filter((event) => (
    (event.event_type || 'installationDateConfirmation') === type
    && statusOf(event.status) !== 'cancelled'
    && stampMs(event.event_date) > 0
  ));
  if (!dated.length) return null;
  return dated.reduce((latest, event) => (
    stampMs(event.event_date) >= stampMs(latest.event_date) ? event : latest
  )).event_date || null;
}

export type OperationalFacts = {
  supplyOnly: boolean;
  latestQuote: OpsDocument | null;
  acceptedQuote: OpsDocument | null;
  /** Accepted quote on file, or a deposit invoice that only exists after acceptance. */
  accepted: boolean;
  acceptedAt: string | null;
  depositInvoice: OpsDocument | null;
  /** A balance invoice was issued with no deposit invoice, so the deposit step was skipped. */
  worksIssued: boolean;
  depositRequired: boolean;
  depositAmount: number;
  /** Earliest evidence the customer received the deposit request (share link or sent email). */
  depositRequestedAt: string | null;
  depositPaid: boolean;
  depositPaidAt: string | null;
  orderPlaced: boolean;
  orderedAt: string | null;
  productionDate: string | null;
  bookingDate: string | null;
  installDone: boolean;
};

export function deriveOperationalFacts(input: {
  project: OpsProject;
  documents: OpsDocument[];
  events: OpsProjectEvent[];
  shareLinks: OpsShareLink[];
}): OperationalFacts {
  const { project, documents, events, shareLinks } = input;
  const supplyOnly = !!project.supply_only;
  const live = documents.filter((doc) => statusOf(doc.status) !== 'void');
  const accepted = acceptedQuote(live);
  const invoices = live.filter((doc) => doc.kind === 'Invoice');

  const depositInvoices = invoices.filter((doc) => normalizeInvoiceRole(doc.invoice_role) === 'deposit');
  const paidDeposit = depositInvoices.find((doc) => isPaidStatus(doc.status)) || null;
  const depositInvoice = paidDeposit || (depositInvoices.length
    ? depositInvoices.reduce((latest, doc) => (stampMs(doc.created_at) >= stampMs(latest.created_at) ? doc : latest))
    : null);
  const worksIssued = invoices.some((doc) => normalizeInvoiceRole(doc.invoice_role) === 'works' && isLiveIssuedInvoice(doc));

  const quoteWantsDeposit = !!accepted && accepted.has_deposit !== false && Number(accepted.deposit_amount || 0) > 0;
  const depositRequired = !!depositInvoice || (quoteWantsDeposit && !worksIssued);
  const depositAmount = Number(depositInvoice?.gross_total || 0) || Number(accepted?.deposit_amount || 0) || 0;

  const depositIds = new Set(depositInvoices.map((doc) => doc.id));
  const depositRequestedAt = earliest([
    ...shareLinks
      .filter((link) => depositIds.has(link.document_id))
      .map((link) => link.last_sent_at || link.created_at),
    ...events
      .filter((event) => event.event_type === 'depositRequest' && statusOf(event.email_status) === 'sent')
      .map((event) => event.email_sent_at || event.event_date || event.created_at),
  ]);

  const tasks = Array.isArray(project.workflow_tasks) ? project.workflow_tasks : [];
  const orderTask = tasks.find((task) => task.task === 'Order Goods' && task.completed);
  // A production date only exists once the supplier has the order
  const productionDate = earliestProductionDate(project);

  return {
    supplyOnly,
    latestQuote: latestQuote(live),
    acceptedQuote: accepted,
    accepted: !!accepted || !!depositInvoice,
    acceptedAt: accepted?.accepted_at || accepted?.paid_at || depositInvoice?.created_at || accepted?.created_at || null,
    depositInvoice,
    worksIssued,
    depositRequired,
    depositAmount,
    depositRequestedAt,
    depositPaid: !!paidDeposit,
    depositPaidAt: paidDeposit?.paid_at || null,
    orderPlaced: !!orderTask || !!productionDate,
    orderedAt: orderTask?.date || null,
    productionDate,
    bookingDate: activeBookingDate(events, supplyOnly),
    installDone: isInstallDone(project),
  };
}

export type OperationalState = {
  id: OperationalStateId;
  queue: OperationalQueueId;
  label: string;
  exception: boolean;
  /** Secondary conditions that did not win the headline. */
  flags: string[];
};

/**
 * Precedence, most advanced fact first. A booking only counts as clean when its
 * prerequisites hold; a missing order outranks a missing deposit because goods
 * cannot be fitted. A production date is taken as evidence the order was placed.
 * Exceptions need commercial acceptance so service visits without a quote stay quiet.
 */
export function deriveOperationalState(facts: OperationalFacts): OperationalState {
  const orderEvidence = facts.orderPlaced || !!facts.productionDate;
  const depositOutstanding = facts.depositRequired && !facts.depositPaid;

  let id: OperationalStateId;
  if (facts.installDone) id = 'installed';
  else if (facts.bookingDate) {
    if (facts.accepted && !orderEvidence) id = 'booked_order_not_placed';
    else if (depositOutstanding) id = 'booked_deposit_not_received';
    else id = 'installation_booked';
  } else if (facts.productionDate) id = 'production_confirmed_not_booked';
  else if (facts.orderPlaced) id = 'ordered_awaiting_production';
  else if (facts.depositPaid) id = 'deposit_received_not_ordered';
  else if (facts.depositRequired) id = facts.depositRequestedAt ? 'deposit_requested' : 'awaiting_deposit_request';
  else if (facts.accepted) id = 'accepted_not_ordered';
  else if (facts.latestQuote) id = 'quoted';
  else id = 'enquiry';

  const meta = OPERATIONAL_STATES[id];
  const flags: string[] = [];
  if (depositOutstanding && id !== 'booked_deposit_not_received' && (meta.queue === 'ordering' || meta.queue === 'installation')) {
    flags.push('Deposit not received');
  }

  return {
    id,
    queue: meta.queue,
    label: operationalStateLabel(id, facts.supplyOnly),
    exception: meta.exception,
    flags,
  };
}
